import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';

/* ── Services (Invoicing → Services) ─────────────────────────── */
export interface ServicePrice { id: string; amount: number; }
export interface OnbService {
  id: string;
  name: string;
  description?: string | null;
  prices: ServicePrice[];
}

/* ── Forms (Forms builder) ───────────────────────────────────── */
export interface OnbForm {
  id: string;
  name: string;
  headline: string;
  fields: any[];
}

/* ── Project templates + folders (Templates page) ────────────── */
export interface OnbTemplateFolder {
  id: string;
  name: string;
  count?: number;
}
export interface OnbProjectTemplate {
  id: string;
  folderId: string | null;
  name: string;
  description?: string | null;
  isPublic: boolean;
  usedCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  ownerName?: string | null;
}

/* Full builder payload stored in the `content` jsonb column. */
export interface OnbTemplateStage { id: string; name: string; tasks: OnbTemplateTask[]; }
export interface OnbTemplateTask { id: string; title: string; assigneeRole?: string | null; done?: boolean; }
export interface OnbTemplateAgendaItem { id: string; text: string; }
export interface OnbTemplateMeeting {
  id: string;
  title: string;
  description?: string;
  durationMins?: number;
  status?: string;                 // Scheduled | Completed | Cancelled …
  attendees?: string[];            // member ids or free-text names
  date?: string;                   // ISO date (optional in a template)
  startTime?: string;              // e.g. "9:00am"
  callInInfo?: string;
  agenda?: OnbTemplateAgendaItem[];
  shareAgenda?: boolean;
  workflowEvent?: string;
  workflowAction?: string;
  internalNotes?: string;
  transcript?: string;
  shareTranscript?: boolean;
  summarizeWithAi?: boolean;
  summary?: string;
  shareSummary?: boolean;
}
export interface OnbTemplateUser { id: string; name: string; email: string; role: string; featured?: boolean; }
export interface OnbTemplateResource { id: string; name: string; url?: string; }
export interface OnbTemplatePortalPage {
  id: string;
  title: string;
  content?: string;           // rich-text HTML
  system?: boolean;
  appEmbed?: string;          // URL or <iframe> code
  formEmbed?: string;         // form embed URL/code
  resources?: OnbTemplateResource[];
}
export interface OnbTemplateFile {
  id: string;
  name: string;
  url?: string;             // data URL for uploaded files (small assets)
  kind?: 'file' | 'folder';
  parentId?: string | null; // folder this item lives in
  fileType?: string;        // e.g. "png", "pdf" (from extension)
  size?: number;
  userInitial?: string;     // uploader avatar initial
  task?: string | null;
  inPortal?: boolean;
  createdAt?: string;
}
export interface OnbTemplateNote { id: string; title: string; body?: string; createdAt?: string; updatedAt?: string; }
export interface OnbTemplateSettings {
  icon?: string;                 // emoji / icon key
  projectOwnerId?: string | null;
  description?: string;
  welcomeEmailSubject?: string;
  welcomeEmailContent?: string;
  fieldProjectOwner?: string;
  fieldProjectName?: string;
}
export interface OnbTemplateContent {
  stages: OnbTemplateStage[];
  meetings: OnbTemplateMeeting[];
  users: OnbTemplateUser[];
  portalPages: OnbTemplatePortalPage[];
  files: OnbTemplateFile[];
  notes: OnbTemplateNote[];
  settings: OnbTemplateSettings;
}
export const DEFAULT_WELCOME_SUBJECT = '{{TEAM_NAME}} has invited you to your client portal.';
export const DEFAULT_WELCOME_CONTENT =
`Hello {{RECIPIENT}},

{{TEAM_NAME}} has invited you to your client portal - your central place for project updates, tasks, and collaboration.

The portal is securely powered by LaunchBay, making it easy to work together, stay informed, and keep your project moving.`;
export const emptyTemplateContent = (): OnbTemplateContent => ({
  stages: [], meetings: [], users: [],
  portalPages: [{ id: 'overview', title: 'Project Overview', content: '', system: true }],
  files: [], notes: [],
  settings: {
    projectOwnerId: null,
    welcomeEmailSubject: DEFAULT_WELCOME_SUBJECT,
    welcomeEmailContent: DEFAULT_WELCOME_CONTENT,
    fieldProjectOwner: '',
    fieldProjectName: '',
  },
});

export interface OnbProjectTemplateFull extends OnbProjectTemplate {
  content: OnbTemplateContent;
}

/* ── Document templates (Documents builder) ──────────────────── */
export interface OnbDocTemplate {
  id: string;
  name: string;
  description: string;
  pdfDataUrl: string | null;
  roles: any[];
  fields: any[];
}

function requireOrgId(): string {
  const orgId = orgStore.getOrgId();
  if (!orgId) throw new Error('No organization found.');
  return orgId;
}
async function requireUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) throw new Error('You must be signed in.');
  return user.id;
}

export const onboardingService = {
  // ── Services ─────────────────────────────────────────────
  async listServices(): Promise<OnbService[]> {
    const { data, error } = await supabase
      .from('onboarding_services')
      .select('id, name, description, prices')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ id: r.id, name: r.name, description: r.description, prices: r.prices ?? [] }));
  },

  async saveService(s: OnbService): Promise<OnbService> {
    // Upsert: update if the id looks like a persisted UUID, else insert.
    const existing = await supabase.from('onboarding_services').select('id').eq('id', s.id).maybeSingle();
    if (!existing.error && existing.data) {
      const { data, error } = await supabase
        .from('onboarding_services')
        .update({ name: s.name, description: s.description ?? null, prices: s.prices, updated_at: new Date().toISOString() })
        .eq('id', s.id)
        .eq('org_id', requireOrgId())
        .select('id, name, description, prices')
        .single();
      if (error) throw error;
      return { id: data.id, name: data.name, description: data.description, prices: data.prices ?? [] };
    }
    const created_by = await requireUserId();
    const { data, error } = await supabase
      .from('onboarding_services')
      .insert({ org_id: requireOrgId(), project_id: projectStore.getProjectId(), name: s.name, description: s.description ?? null, prices: s.prices, created_by })
      .select('id, name, description, prices')
      .single();
    if (error) throw error;
    return { id: data.id, name: data.name, description: data.description, prices: data.prices ?? [] };
  },

  async deleteService(id: string): Promise<void> {
    const { error } = await supabase
      .from('onboarding_services')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  // ── Forms ────────────────────────────────────────────────
  async listForms(): Promise<OnbForm[]> {
    const { data, error } = await supabase
      .from('onboarding_forms')
      .select('id, name, headline')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    // `fields` is omitted from the list fetch (can be large) — loaded on demand.
    return (data ?? []).map((r: any) => ({ id: r.id, name: r.name, headline: r.headline ?? '', fields: [] }));
  },

  async getForm(id: string): Promise<OnbForm | null> {
    const { data, error } = await supabase.from('onboarding_forms').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return { id: data.id, name: data.name, headline: data.headline ?? '', fields: data.fields ?? [] };
  },

  async createForm(f: Omit<OnbForm, 'id'>): Promise<OnbForm> {
    const created_by = await requireUserId();
    const { data, error } = await supabase
      .from('onboarding_forms')
      .insert({ org_id: requireOrgId(), project_id: projectStore.getProjectId(), name: f.name, headline: f.headline, fields: f.fields, created_by })
      .select('id, name, headline, fields')
      .single();
    if (error) throw error;
    return { id: data.id, name: data.name, headline: data.headline ?? '', fields: data.fields ?? [] };
  },

  async updateForm(f: OnbForm): Promise<void> {
    const { error } = await supabase
      .from('onboarding_forms')
      .update({ name: f.name, headline: f.headline, fields: f.fields, updated_at: new Date().toISOString() })
      .eq('id', f.id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  async deleteForm(id: string): Promise<void> {
    const { error } = await supabase
      .from('onboarding_forms')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  // ── Template folders ─────────────────────────────────────
  async listTemplateFolders(): Promise<OnbTemplateFolder[]> {
    const { data, error } = await supabase
      .from('onboarding_template_folders')
      .select('id, name')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ id: r.id, name: r.name }));
  },

  async createTemplateFolder(name: string): Promise<OnbTemplateFolder> {
    const created_by = await requireUserId();
    const { data, error } = await supabase
      .from('onboarding_template_folders')
      .insert({ org_id: requireOrgId(), name, created_by })
      .select('id, name')
      .single();
    if (error) throw error;
    return { id: data.id, name: data.name };
  },

  async renameTemplateFolder(id: string, name: string): Promise<void> {
    const { error } = await supabase
      .from('onboarding_template_folders')
      .update({ name, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  async deleteTemplateFolder(id: string): Promise<void> {
    const { error } = await supabase
      .from('onboarding_template_folders')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  // ── Project templates ────────────────────────────────────
  async listProjectTemplates(scope: 'mine' | 'public'): Promise<OnbProjectTemplate[]> {
    let query = supabase
      .from('onboarding_project_templates')
      .select('id, folder_id, name, description, is_public, used_count, last_used_at, created_at')
      .order('created_at', { ascending: false });
    if (scope === 'public') query = query.eq('is_public', true);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map((r: any) => ({
      id: r.id, folderId: r.folder_id, name: r.name, description: r.description,
      isPublic: r.is_public, usedCount: r.used_count ?? 0, lastUsedAt: r.last_used_at,
      createdAt: r.created_at,
    }));
  },

  async getProjectTemplate(id: string): Promise<OnbProjectTemplateFull | null> {
    const { data, error } = await supabase
      .from('onboarding_project_templates')
      .select('id, folder_id, name, description, is_public, used_count, last_used_at, created_at, content')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const base = emptyTemplateContent();
    const raw = (data.content ?? {}) as Partial<OnbTemplateContent>;
    const content = { ...base, ...raw, settings: { ...base.settings, ...(raw.settings ?? {}) } } as OnbTemplateContent;
    return {
      id: data.id, folderId: data.folder_id, name: data.name, description: data.description,
      isPublic: data.is_public, usedCount: data.used_count ?? 0, lastUsedAt: data.last_used_at,
      createdAt: data.created_at, content,
    };
  },

  async saveTemplateContent(id: string, content: OnbTemplateContent): Promise<void> {
    const { error } = await supabase
      .from('onboarding_project_templates')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  async createProjectTemplate(input: { name: string; folderId: string | null; description?: string | null; isPublic?: boolean }): Promise<OnbProjectTemplate> {
    const created_by = await requireUserId();
    const { data, error } = await supabase
      .from('onboarding_project_templates')
      .insert({ org_id: requireOrgId(), folder_id: input.folderId, name: input.name, description: input.description ?? null, is_public: !!input.isPublic, created_by })
      .select('id, folder_id, name, description, is_public, used_count, last_used_at, created_at')
      .single();
    if (error) throw error;
    return { id: data.id, folderId: data.folder_id, name: data.name, description: data.description, isPublic: data.is_public, usedCount: data.used_count ?? 0, lastUsedAt: data.last_used_at, createdAt: data.created_at };
  },

  async updateProjectTemplate(id: string, updates: { name?: string; folderId?: string | null; description?: string | null; isPublic?: boolean }): Promise<void> {
    const patch: Record<string, unknown> = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) patch.name = updates.name;
    if (updates.folderId !== undefined) patch.folder_id = updates.folderId;
    if (updates.description !== undefined) patch.description = updates.description;
    if (updates.isPublic !== undefined) patch.is_public = updates.isPublic;
    const { error } = await supabase
      .from('onboarding_project_templates')
      .update(patch)
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  async deleteProjectTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('onboarding_project_templates')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  // Copy a public template into the current org as a private ("your") template.
  async saveTemplateToMine(sourceId: string): Promise<OnbProjectTemplate> {
    const src = await this.getProjectTemplate(sourceId);
    if (!src) throw new Error('Template not found.');
    const created = await this.createProjectTemplate({ name: src.name, folderId: null, description: src.description, isPublic: false });
    await this.saveTemplateContent(created.id, src.content);
    return created;
  },

  // ── Document templates ───────────────────────────────────
  async listTemplates(): Promise<OnbDocTemplate[]> {
    // Omit heavy pdf_data from the list; loaded on demand in getTemplate.
    const { data, error } = await supabase
      .from('onboarding_doc_templates')
      .select('id, name, description, roles, fields')
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ id: r.id, name: r.name, description: r.description ?? '', pdfDataUrl: null, roles: r.roles ?? [], fields: r.fields ?? [] }));
  },

  async getTemplate(id: string): Promise<OnbDocTemplate | null> {
    const { data, error } = await supabase
      .from('onboarding_doc_templates')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return { id: data.id, name: data.name, description: data.description ?? '', pdfDataUrl: data.pdf_data ?? null, roles: data.roles ?? [], fields: data.fields ?? [] };
  },

  async createTemplate(t: Omit<OnbDocTemplate, 'id'>): Promise<OnbDocTemplate> {
    const created_by = await requireUserId();
    const { data, error } = await supabase
      .from('onboarding_doc_templates')
      .insert({ org_id: requireOrgId(), project_id: projectStore.getProjectId(), name: t.name, description: t.description, pdf_data: t.pdfDataUrl, roles: t.roles, fields: t.fields, created_by })
      .select('*')
      .single();
    if (error) throw error;
    return { id: data.id, name: data.name, description: data.description ?? '', pdfDataUrl: data.pdf_data ?? null, roles: data.roles ?? [], fields: data.fields ?? [] };
  },

  async updateTemplate(t: OnbDocTemplate): Promise<void> {
    const { error } = await supabase
      .from('onboarding_doc_templates')
      .update({ name: t.name, description: t.description, pdf_data: t.pdfDataUrl, roles: t.roles, fields: t.fields, updated_at: new Date().toISOString() })
      .eq('id', t.id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },

  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('onboarding_doc_templates')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },
};
